import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { toast } from "sonner";
import {
  ArrowRight,
  CheckCircle,
  Users,
  Phone,
  Headphones,
  Award,
  Calendar,
  Clock,
  BookOpen,
  MessageSquare,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import useFormValidation from "../../hooks/useFormValidation";
import SEO from "../../components/SEO";
import StructuredData from "../../components/StructuredData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConciergeFrontline = () => {
  const [searchParams] = useSearchParams();
  const [showApplication, setShowApplication] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [formStep, setFormStep] = useState(1);
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState("");
  const [discountLoading, setDiscountLoading] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState("");
  
  const BASE_PRICE = 2200;
  
  const calculateTotal = () => {
    if (!appliedDiscount) return BASE_PRICE;
    
    if (appliedDiscount.discount_type === "percentage") {
      return Math.max(0, BASE_PRICE - (BASE_PRICE * appliedDiscount.discount_value / 100));
    } else {
      return Math.max(0, BASE_PRICE - appliedDiscount.discount_value);
    }
  };

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) return;
    
    setDiscountLoading(true);
    setDiscountError("");
    
    try {
      const params = new URLSearchParams({
        code: discountCode.trim(),
        program_type: "frontline_concierge",
        amount: BASE_PRICE.toString()
      });
      
      const response = await fetch(`${API}/programs/validate-discount?${params.toString()}`, {
        method: "POST"
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Invalid discount code");
      }
      
      setAppliedDiscount(data);
      toast.success(`Discount code "${data.code}" applied!`);
    } catch (error) {
      setDiscountError(error.message);
      setAppliedDiscount(null);
    } finally {
      setDiscountLoading(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode("");
    setDiscountError("");
  };

  const [formData, setFormData] = useState({
    // Practice & Contact Information
    applicant_name: "",
    applicant_email: "",
    contact_phone: "",
    company_name: "",
    practice_location: "",
    role_in_organization: "",
    // Front Desk Team Details
    num_front_desk: "",
    front_desk_responsibilities: "",
    front_desk_challenges: "",
    // Patient Experience & Operations
    first_contact_handling: "",
    check_in_process: "",
    complaint_handling: "",
    // Goals & Pain Points
    top_priorities: "",
    patient_complaints: "",
    skills_to_develop: "",
    // Team Culture & Training
    team_culture: "",
    prior_training: "",
    open_to_training: "",
    // Engagement & Readiness
    leadership_support: "",
    can_dedicate_time: "",
    // Contact & Next Steps
    contact_person: "",
    anything_else: "",
    // Legacy fields for API compatibility
    company_size: "",
    industry: "healthcare",
    challenges: "",
    goals: "",
    timeline: "4-6 weeks",
    budget: "",
  });

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const paymentParam = searchParams.get("payment");

    if (sessionId && paymentParam === "success") {
      pollPaymentStatus(sessionId);
    } else if (paymentParam === "cancelled") {
      toast.error("Payment was cancelled");
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      setPaymentStatus("timeout");
      return;
    }

    try {
      const response = await fetch(`${API}/programs/payment-status/${sessionId}`);
      const data = await response.json();

      if (data.payment_status === "paid") {
        setPaymentStatus("success");
        toast.success("Payment successful! Welcome to Concierge Frontline!");
      } else if (data.status === "expired") {
        setPaymentStatus("expired");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  // Validation configuration
  const validationConfig = {
    requiredFieldsByStep: {
      1: ["applicant_name", "applicant_email", "contact_phone", "company_name", "practice_location", "role_in_organization"],
      2: ["num_front_desk", "front_desk_responsibilities", "front_desk_challenges"],
      3: ["top_priorities", "patient_complaints", "skills_to_develop"],
      4: ["team_culture", "open_to_training", "leadership_support", "can_dedicate_time"],
    },
    fieldLabels: {
      applicant_name: "Your Name",
      applicant_email: "Email Address",
      contact_phone: "Phone Number",
      company_name: "Practice Name",
      practice_location: "Practice Location",
      role_in_organization: "Your Role",
      num_front_desk: "Number of Front Desk Staff",
      front_desk_responsibilities: "Front Desk Responsibilities",
      front_desk_challenges: "Front Desk Challenges",
      top_priorities: "Top Priorities",
      patient_complaints: "Common Patient Complaints",
      skills_to_develop: "Skills to Develop",
      team_culture: "Team Culture",
      open_to_training: "Training Openness",
      leadership_support: "Leadership Support",
      can_dedicate_time: "Time Commitment",
    },
    emailFields: ["applicant_email"],
  };

  const { 
    validationErrors, 
    validateStep, 
    validateAllSteps, 
    clearFieldError 
  } = useFormValidation(formData, validationConfig);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearFieldError(field);
  };

  const nextStep = () => {
    if (validateStep(formStep)) {
      setFormStep(prev => Math.min(prev + 1, 4));
    } else {
      toast.error("Please fill in all required fields before continuing");
    }
  };

  const prevStep = () => setFormStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateAllSteps()) {
      toast.error("Please fill in all required fields before submitting");
      return;
    }
    
    setLoading(true);

    try {
      const applicationData = {
        program_type: "frontline_concierge",
        applicant_name: formData.applicant_name,
        applicant_email: formData.applicant_email,
        company_name: formData.company_name,
        company_size: formData.num_front_desk || formData.company_size,
        industry: formData.industry,
        challenges: `Front Desk Challenges: ${formData.front_desk_challenges}\n\nTop Priorities: ${formData.top_priorities}\n\nPatient Complaints: ${formData.patient_complaints}`,
        goals: `Skills to Develop: ${formData.skills_to_develop}\n\nTeam Culture: ${formData.team_culture}`,
        timeline: formData.timeline,
        budget: formData.budget,
        contact_phone: formData.contact_phone,
      };

      const params = new URLSearchParams(applicationData);

      const response = await fetch(`${API}/programs/apply?${params.toString()}`, {
        method: "POST",
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.detail || "Application failed");

      setApplicationId(data.application_id);
      toast.success("Application submitted!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!applicationId) return;
    setLoading(true);

    try {
      const params = new URLSearchParams();
      if (appliedDiscount) params.append("discount_code", appliedDiscount.code);
      
      const queryString = params.toString();
      const url = `${API}/programs/checkout/${applicationId}${queryString ? `?${queryString}` : ""}`;
      
      const response = await fetch(url, {
        method: "POST",
        headers: { origin: window.location.origin },
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.detail || "Checkout failed");

      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message);
      setLoading(false);
    }
  };

  const whatsIncluded = [
    { 
      icon: BookOpen, 
      title: "Custom Training Materials", 
      description: "Tailored specifically to your practice's workflows and patient types" 
    },
    { 
      icon: MessageSquare, 
      title: "Two Live Virtual Training Sessions", 
      description: "Interactive sessions for your front desk team (~1 hour each)" 
    },
    { 
      icon: Clock, 
      title: "30 Days Follow-Up Support", 
      description: "Adjustments and guidance as your team implements new practices" 
    },
    { 
      icon: Award, 
      title: "Tools & Confidence Building", 
      description: "Scripts, checklists, and resources your team can use daily" 
    },
  ];

  const benefits = [
    "Welcome patients with confidence and warmth",
    "Handle busy periods without feeling overwhelmed",
    "Create smooth, memorable patient experiences",
    "Reduce no-shows with better communication",
    "Build consistency across your entire front desk team",
  ];

  const targetAudiences = [
    "Medical clinics seeking a friendlier patient experience",
    "Dental offices wanting smoother check-ins and check-outs",
    "Therapy practices needing better phone and scheduling systems",
    "Specialty clinics where front desk handles multiple tasks",
    "Any practice wanting to reduce front desk overwhelm",
  ];

  const faqs = [
    {
      question: "How long does the program take?",
      answer: "CFE is a 4-6 week program designed to fit busy practice schedules. Each training session lasts approximately one hour, and teams typically spend less than 30 minutes per week on follow-ups or feedback.",
    },
    {
      question: "Is this program virtual or in-person?",
      answer: "All sessions are conducted virtually, eliminating travel or workplace disruption. This makes it easy for your team to participate without impacting patient care.",
    },
    {
      question: "What happens if we hire new staff after the program?",
      answer: "All program materials are yours to keep and reuse. When new team members join, we can provide quick refreshers or share recorded sessions to ensure consistency across your team.",
    },
    {
      question: "Do you offer ongoing support after the program?",
      answer: "Yes! Optional ongoing support is available for $500 per month if your practice needs continued assistance beyond the initial 30-day follow-up period.",
    },
    {
      question: "What size practice is this designed for?",
      answer: "CFE is designed for small healthcare practices where front desk staff handle multiple tasks and patient interactions. It works great for practices with 1-5 front desk team members.",
    },
  ];

  const renderFormStep = () => {
    switch (formStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2">
              Practice & Contact Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Your Name *</Label>
                <Input
                  value={formData.applicant_name}
                  onChange={(e) => handleChange("applicant_name", e.target.value)}
                  placeholder="Full name"
                  required
                  data-testid="cf-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.applicant_email}
                  onChange={(e) => handleChange("applicant_email", e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="cf-email"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone Number *</Label>
                <Input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => handleChange("contact_phone", e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Practice Name *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => handleChange("company_name", e.target.value)}
                  placeholder="Your practice name"
                  required
                  data-testid="cf-practice"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Practice Location (City, State) *</Label>
                <Input
                  value={formData.practice_location}
                  onChange={(e) => handleChange("practice_location", e.target.value)}
                  placeholder="Atlanta, GA"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Your Role in the Organization *</Label>
                <Select
                  value={formData.role_in_organization}
                  onValueChange={(value) => handleChange("role_in_organization", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your role..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner/Founder</SelectItem>
                    <SelectItem value="practice-manager">Practice Manager</SelectItem>
                    <SelectItem value="office-manager">Office Manager</SelectItem>
                    <SelectItem value="clinical-director">Clinical Director</SelectItem>
                    <SelectItem value="hr">HR/People Operations</SelectItem>
                    <SelectItem value="front-desk-lead">Front Desk Lead</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2 mt-6">
              Front Desk Team Details
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>How many front desk/reception team members do you have? *</Label>
                <Select
                  value={formData.num_front_desk}
                  onValueChange={(value) => handleChange("num_front_desk", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 person</SelectItem>
                    <SelectItem value="2">2 people</SelectItem>
                    <SelectItem value="3">3 people</SelectItem>
                    <SelectItem value="4-5">4-5 people</SelectItem>
                    <SelectItem value="6+">6+ people</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>What are their typical responsibilities? *</Label>
              <Textarea
                value={formData.front_desk_responsibilities}
                onChange={(e) => handleChange("front_desk_responsibilities", e.target.value)}
                placeholder="e.g., answering phones, scheduling, check-in/check-out, insurance verification, payment collection..."
                rows={3}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>What are the main challenges your front desk team is facing? *</Label>
              <Textarea
                value={formData.front_desk_challenges}
                onChange={(e) => handleChange("front_desk_challenges", e.target.value)}
                placeholder="e.g., high call volume, multitasking stress, inconsistent service, patient complaints..."
                rows={3}
                required
                data-testid="cf-challenges"
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2">
              Patient Experience & Operations
            </h3>
            <div className="space-y-2">
              <Label>How do you currently handle first patient contact (calls, emails, walk-ins)? *</Label>
              <Textarea
                value={formData.first_contact_handling}
                onChange={(e) => handleChange("first_contact_handling", e.target.value)}
                placeholder="Describe your current process for handling new patient inquiries and contacts..."
                rows={3}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>What does a typical patient arrival and check-in process look like? *</Label>
              <Textarea
                value={formData.check_in_process}
                onChange={(e) => handleChange("check_in_process", e.target.value)}
                placeholder="Walk us through what happens from when a patient arrives to when they're seen..."
                rows={3}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>How are patient problems, complaints, or escalations managed today?</Label>
              <Textarea
                value={formData.complaint_handling}
                onChange={(e) => handleChange("complaint_handling", e.target.value)}
                placeholder="Describe how your team handles difficult situations or unhappy patients..."
                rows={3}
              />
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2">
              Goals & Pain Points
            </h3>
            <div className="space-y-2">
              <Label>What are your top 2-3 priorities for improving your front desk operations? *</Label>
              <Textarea
                value={formData.top_priorities}
                onChange={(e) => handleChange("top_priorities", e.target.value)}
                placeholder="What would you most like to improve or change?"
                rows={3}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Have you received any common patient complaints or feedback about the front desk experience?</Label>
              <Textarea
                value={formData.patient_complaints}
                onChange={(e) => handleChange("patient_complaints", e.target.value)}
                placeholder="Describe any recurring feedback or complaints you've received..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Are there specific skills or areas of support you want your front desk team to develop? *</Label>
              <Textarea
                value={formData.skills_to_develop}
                onChange={(e) => handleChange("skills_to_develop", e.target.value)}
                placeholder="e.g., phone etiquette, conflict resolution, time management, patient communication..."
                rows={3}
                required
              />
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2">
              Team Culture & Training
            </h3>
            <div className="space-y-2">
              <Label>How would you describe your front desk team&apos;s culture or dynamic?</Label>
              <Textarea
                value={formData.team_culture}
                onChange={(e) => handleChange("team_culture", e.target.value)}
                placeholder="e.g., collaborative, supportive, stressed, siloed..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Have your team members received any prior training for front desk or customer service roles?</Label>
              <Select
                value={formData.prior_training}
                onValueChange={(value) => handleChange("prior_training", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No prior training</SelectItem>
                  <SelectItem value="informal">Informal/on-the-job training only</SelectItem>
                  <SelectItem value="some">Some formal training</SelectItem>
                  <SelectItem value="extensive">Extensive training programs</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Are you open to ongoing training and feedback for your front desk staff? *</Label>
              <Select
                value={formData.open_to_training}
                onValueChange={(value) => handleChange("open_to_training", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes, absolutely</SelectItem>
                  <SelectItem value="somewhat">Somewhat - depends on time commitment</SelectItem>
                  <SelectItem value="limited">Limited - only this program</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2 mt-6">
              Engagement & Readiness
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Is your leadership team supportive of investing in front desk training? *</Label>
                <Select
                  value={formData.leadership_support}
                  onValueChange={(value) => handleChange("leadership_support", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fully">Fully supportive</SelectItem>
                    <SelectItem value="mostly">Mostly supportive</SelectItem>
                    <SelectItem value="somewhat">Somewhat supportive</SelectItem>
                    <SelectItem value="undecided">Still deciding</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Can you dedicate time for virtual training sessions? *</Label>
                <Select
                  value={formData.can_dedicate_time}
                  onValueChange={(value) => handleChange("can_dedicate_time", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes, we can commit</SelectItem>
                    <SelectItem value="limited">Limited availability</SelectItem>
                    <SelectItem value="flexible">Need flexible scheduling</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Who should we contact for follow-up and scheduling?</Label>
              <Input
                value={formData.contact_person}
                onChange={(e) => handleChange("contact_person", e.target.value)}
                placeholder="Name and preferred contact method"
              />
            </div>
            <div className="space-y-2">
              <Label>Is there anything else you&apos;d like us to know about your front desk team or goals?</Label>
              <Textarea
                value={formData.anything_else}
                onChange={(e) => handleChange("anything_else", e.target.value)}
                placeholder="Any additional information..."
                rows={3}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (paymentStatus === "success") {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-rnb-light to-white">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-rnb-navy/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-rnb-navy" />
          </div>
          <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-4">
            Welcome to Concierge Frontline!
          </h2>
          <p className="text-rnb-gray mb-6">
            Your enrollment is complete. Our team will contact you within 24 hours 
            to schedule your first training session.
          </p>
          <Link to="/portal">
            <Button className="btn-hover-lift">
              Go to Client Portal
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO 
        title="Concierge Frontline — Front Desk & Patient Experience Training"
        description="Your front desk shapes the patient experience. Concierge Frontline trains your team to handle intake, phone calls, and patient interactions with confidence and professionalism. $2,200."
        ogImage="https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/7ckey7kt_Primary%20Care%20provider%20with%20patient%20at%20appointment.png.webp"
        keywords="medical front desk training, how to improve front desk in clinic, patient experience training healthcare, receptionist training medical office, front office staff improvement, medical office customer service"
      />
      <StructuredData data={[{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
          { "@type": "ListItem", "position": 2, "name": "Programs", "item": "https://www.rnbconsultinggroup.com/services" },
          { "@type": "ListItem", "position": 3, "name": "Concierge Frontline", "item": "https://www.rnbconsultinggroup.com/programs/concierge-frontline" }
        ]
      }]} />
      
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-rnb-navy to-rnb-navy/90 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-teal/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-rnb-blue/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full mb-6">
                <Headphones className="w-4 h-4 text-rnb-teal mr-2" />
                <span className="text-sm font-medium text-white">Front Desk Training Program</span>
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Concierge Frontline Experience™
              </h1>

              <p className="text-xl text-slate-200 font-medium mb-4">
                Upgrade Your Small Healthcare Practice
              </p>

              <p className="text-lg text-slate-300 mb-8">
                A 4-6 week virtual training program designed to improve front desk operations 
                and patient experience. Custom materials, live sessions, and 30 days of 
                follow-up support.
              </p>

              <div className="flex items-end gap-4 mb-8">
                <div>
                  <p className="text-sm text-slate-400 mb-1">One-time fee</p>
                  <p className="font-mono-numbers text-4xl font-bold text-white">$2,200</p>
                </div>
                <p className="text-sm text-slate-400 pb-1">4-6 weeks • Virtual</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="btn-hover-lift bg-rnb-teal hover:bg-rnb-teal/90"
                  onClick={() => setShowApplication(true)}
                  data-testid="frontline-apply-btn"
                >
                  Enroll Now – $2,200
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Link to="/contact">
                  <Button variant="outline" size="lg" className="text-white border-white/30 hover:bg-white/10">
                    <Calendar className="mr-2 h-4 w-4" />
                    Schedule Free Consultation
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative hidden lg:block">
              <img
                src="https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/7ckey7kt_Primary%20Care%20provider%20with%20patient%20at%20appointment.png.webp"
                alt="Healthcare provider smiling warmly while assisting a patient"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              What&apos;s Included
            </h2>
            <p className="text-rnb-gray max-w-2xl mx-auto">
              Everything you need to transform your front office experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whatsIncluded.map((item, index) => (
              <Card key={item.title || item.name || item.label || index} className="card-hover border-0 shadow-sm">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-rnb-navy/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-rnb-navy" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-rnb-gray">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-4">
              By Completion, Your Front Desk Team Will...
            </h2>
          </div>
          <div className="space-y-4">
            {benefits.map((benefit, index) => (
              <div key={benefit.title || benefit.label || i} className="flex items-center gap-4 p-5 bg-white rounded-xl shadow-sm">
                <CheckCircle className="w-6 h-6 text-rnb-teal flex-shrink-0" />
                <span className="text-lg text-rnb-navy">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-8 text-center">
            Is Concierge Frontline Right for You?
          </h2>
          <div className="space-y-4">
            {targetAudiences.map((item, index) => (
              <div key={item.title || item.name || item.label || index} className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <Users className="w-5 h-5 text-rnb-blue flex-shrink-0 mt-0.5" />
                <span className="text-rnb-navy">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Timeline */}
      <section className="py-24 bg-rnb-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-12 text-center">
            How It Works
          </h2>
          <div className="space-y-8">
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 bg-rnb-navy rounded-full flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-white">1</span>
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-2">Discovery Call</h3>
                <p className="text-rnb-gray">We learn about your practice, current challenges, and goals for your front desk team.</p>
              </div>
            </div>
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 bg-rnb-navy rounded-full flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-white">2</span>
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-2">Custom Materials Development</h3>
                <p className="text-rnb-gray">We create training materials tailored specifically to your practice&apos;s workflows and patient types.</p>
              </div>
            </div>
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 bg-rnb-navy rounded-full flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-white">3</span>
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-2">Live Training Sessions</h3>
                <p className="text-rnb-gray">Two interactive virtual sessions (~1 hour each) to train your front desk team on new practices.</p>
              </div>
            </div>
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 bg-rnb-teal rounded-full flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-white">4</span>
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-2">30 Days Follow-Up Support</h3>
                <p className="text-rnb-gray">Ongoing adjustments and guidance as your team implements what they&apos;ve learned.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Card className="border-2 border-rnb-navy overflow-hidden">
            <div className="bg-rnb-navy text-white py-4">
              <h3 className="font-heading text-xl font-semibold">Concierge Frontline Experience™</h3>
            </div>
            <CardContent className="p-8">
              <p className="font-mono-numbers text-5xl font-bold text-rnb-navy mb-2">$2,200</p>
              <p className="text-rnb-gray mb-6">One-time fee • 4-6 weeks • Fully virtual</p>
              
              <div className="text-left space-y-3 mb-8">
                {whatsIncluded.map((item, index) => (
                  <div key={item.title || item.name || item.label || index} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-rnb-teal flex-shrink-0" />
                    <span className="text-rnb-navy">{item.title}</span>
                  </div>
                ))}
              </div>

              <div className="border-t pt-6 mb-6">
                <p className="text-sm text-rnb-gray mb-2">Need ongoing support?</p>
                <p className="font-medium text-rnb-navy">Optional: $500/month for continued assistance</p>
              </div>
              
              <Button
                size="lg"
                className="w-full btn-hover-lift"
                onClick={() => setShowApplication(true)}
              >
                Enroll Now – $2,200
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={faq.question || faq.title || i} value={`faq-${index}`} className="border rounded-lg px-4 bg-white">
                <AccordionTrigger className="text-left font-medium text-rnb-navy hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-rnb-gray">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-rnb-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Patient Experience?
          </h2>
          <p className="text-slate-300 mb-8">
            Give your front desk team the tools and confidence they need to create 
            exceptional patient experiences.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="btn-hover-lift bg-rnb-teal hover:bg-rnb-teal/90"
              onClick={() => setShowApplication(true)}
            >
              Enroll Now – $2,200
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Link to="/contact">
              <Button variant="outline" size="lg" className="text-white border-white/30 hover:bg-white/10">
                Schedule Free Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Application Modal */}
      {showApplication && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardContent className="p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-rnb-navy">
                    {applicationId ? "Complete Your Enrollment" : "Concierge Frontline Application"}
                  </h2>
                  <p className="text-rnb-gray text-sm mt-1">
                    {applicationId
                      ? "Your application has been submitted. Click below to complete payment."
                      : `Step ${formStep} of 4`}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowApplication(false);
                    setApplicationId(null);
                    setFormStep(1);
                  }}
                  className="text-rnb-gray hover:text-rnb-navy text-xl"
                >
                  ✕
                </button>
              </div>

              {applicationId ? (
                <div className="py-6">
                  <div className="text-center mb-6">
                    <CheckCircle className="w-16 h-16 text-rnb-navy mx-auto mb-4" />
                    <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-2">
                      Application Submitted!
                    </h3>
                    <p className="text-rnb-gray">
                      Complete your enrollment below.
                    </p>
                  </div>

                  {/* Discount Code Section */}
                  <div className="border rounded-lg p-4 mb-4 bg-slate-50">
                    <p className="text-sm font-medium text-rnb-navy mb-3">Have a discount code?</p>
                    {appliedDiscount ? (
                      <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                        <div>
                          <span className="text-green-700 font-medium">{appliedDiscount.code}</span>
                          <span className="text-green-600 text-sm ml-2">
                            ({appliedDiscount.discount_type === "percentage" 
                              ? `${appliedDiscount.discount_value}% off` 
                              : `$${appliedDiscount.discount_value} off`})
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={removeDiscount}
                          className="text-green-700 hover:text-green-900 text-sm underline"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter code"
                          value={discountCode}
                          onChange={(e) => {
                            setDiscountCode(e.target.value.toUpperCase());
                            setDiscountError("");
                          }}
                          className="flex-1"
                          data-testid="cf-discount-code-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={validateDiscountCode}
                          disabled={discountLoading || !discountCode.trim()}
                          data-testid="cf-apply-discount-btn"
                        >
                          {discountLoading ? "..." : "Apply"}
                        </Button>
                      </div>
                    )}
                    {discountError && (
                      <p className="text-red-500 text-sm mt-2">{discountError}</p>
                    )}
                  </div>

                  {/* Price Summary */}
                  <div className="border rounded-lg p-4 mb-6 bg-white">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-rnb-gray">Concierge Frontline Experience™</span>
                        <span className="font-mono-numbers text-rnb-navy">$2,200</span>
                      </div>
                      {appliedDiscount && (
                        <div className="flex justify-between text-green-600">
                          <span>Discount ({appliedDiscount.code})</span>
                          <span className="font-mono-numbers">
                            -${(BASE_PRICE - calculateTotal()).toLocaleString()}
                          </span>
                        </div>
                      )}
                      <div className="border-t pt-2 flex justify-between font-bold">
                        <span className="text-rnb-navy">Total</span>
                        <span className="font-mono-numbers text-xl text-rnb-navy">${calculateTotal().toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    size="lg"
                    className="w-full btn-hover-lift"
                    onClick={handlePayment}
                    disabled={loading}
                    data-testid="frontline-pay-btn"
                  >
                    {loading ? "Processing..." : `Pay $${calculateTotal().toLocaleString()} to Enroll`}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Progress indicator */}
                  <div className="flex items-center justify-between mb-6">
                    {[1, 2, 3, 4].map((step) => (
                      <div key={step} className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          formStep >= step ? 'bg-rnb-navy text-white' : 'bg-slate-200 text-slate-500'
                        }`}>
                          {step}
                        </div>
                        {step < 4 && (
                          <div className={`w-12 sm:w-20 h-1 mx-1 ${
                            formStep > step ? 'bg-rnb-navy' : 'bg-slate-200'
                          }`} />
                        )}
                      </div>
                    ))}
                  </div>

                  {renderFormStep()}

                  <div className="flex justify-between pt-4">
                    {formStep > 1 ? (
                      <Button type="button" variant="outline" onClick={prevStep}>
                        <ChevronLeft className="mr-2 h-4 w-4" />
                        Previous
                      </Button>
                    ) : (
                      <div />
                    )}
                    
                    {formStep < 4 ? (
                      <Button type="button" onClick={nextStep}>
                        Next
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    ) : (
                      <Button type="submit" className="btn-hover-lift" disabled={loading}>
                        {loading ? "Submitting..." : "Submit Application"}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ConciergeFrontline;
